(() => {
  const routes = (window.theme && window.theme.routes) || {};
  const strings = (window.theme && window.theme.strings) || {};
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  const parseHTML = (html) => new DOMParser().parseFromString(html, 'text/html');

  const postJSON = (url, body) =>
    fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body),
    });

  const updateCartCount = (count) => {
    document.querySelectorAll('[data-cart-count-bubble]').forEach((bubble) => {
      bubble.textContent = count;
      bubble.hidden = count === 0;
    });
  };

  /* Drawers: menu, search and cart
     ------------------------------------------------------------------------ */

  class DrawerPanel extends HTMLElement {
    connectedCallback() {
      this.panel = this.querySelector('.drawer__panel');
      this.addEventListener('click', (event) => {
        if (event.target.closest('[data-drawer-close]')) this.close();
      });
      this.addEventListener('keydown', (event) => {
        if (event.key === 'Escape') this.close();
        if (event.key === 'Tab') this.trapFocus(event);
      });
    }

    get isOpen() {
      return this.classList.contains('is-open');
    }

    focusable() {
      const selector = 'a[href], button:not([disabled]), input:not([type="hidden"]):not([disabled]), select, textarea, summary';
      return [...this.querySelectorAll(selector)].filter((el) => el.offsetParent !== null);
    }

    trapFocus(event) {
      const items = this.focusable();
      if (!items.length) return;
      const first = items[0];
      const last = items[items.length - 1];
      if (event.shiftKey && (document.activeElement === first || document.activeElement === this.panel)) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }

    open(opener) {
      if (this.isOpen) return;
      clearTimeout(this.hideTimer);
      this.opener = opener || document.activeElement;
      this.hidden = false;
      // Force a reflow so the transition runs from the hidden state.
      void this.offsetWidth;
      this.classList.add('is-open');
      document.documentElement.classList.add('has-drawer');
      (this.querySelector('[data-autofocus]') || this.panel).focus({ preventScroll: true });
    }

    close() {
      if (!this.isOpen) return;
      this.classList.remove('is-open');
      document.documentElement.classList.remove('has-drawer');
      this.hideTimer = setTimeout(() => (this.hidden = true), reducedMotion.matches ? 0 : 400);
      if (this.opener && this.opener.isConnected) this.opener.focus({ preventScroll: true });
    }
  }

  customElements.define('drawer-panel', DrawerPanel);

  document.addEventListener('click', (event) => {
    const opener = event.target.closest('[data-drawer-open]');
    if (!opener) return;
    const drawer = document.getElementById(opener.dataset.drawerOpen);
    if (!drawer || typeof drawer.open !== 'function') return;
    event.preventDefault();
    drawer.open(opener);
  });

  /* Header
     ------------------------------------------------------------------------ */

  class HeaderBar extends HTMLElement {
    connectedCallback() {
      const setHeight = () =>
        document.documentElement.style.setProperty('--header-h', `${this.offsetHeight}px`);
      setHeight();
      new ResizeObserver(setHeight).observe(this);

      const onScroll = () => this.classList.toggle('is-scrolled', window.scrollY > 8);
      onScroll();
      window.addEventListener('scroll', onScroll, { passive: true });

      // Dropdown menus: close on outside click or Escape.
      document.addEventListener('click', (event) => {
        this.querySelectorAll('.nav-dropdown[open]').forEach((dropdown) => {
          if (!dropdown.contains(event.target)) dropdown.open = false;
        });
      });
      this.addEventListener('keydown', (event) => {
        if (event.key !== 'Escape') return;
        const dropdown = event.target.closest('.nav-dropdown[open]');
        if (!dropdown) return;
        dropdown.open = false;
        dropdown.querySelector('summary').focus();
      });
    }
  }

  customElements.define('header-bar', HeaderBar);

  /* Quantity stepper
     ------------------------------------------------------------------------ */

  class QuantityInput extends HTMLElement {
    connectedCallback() {
      this.addEventListener('click', (event) => {
        const button = event.target.closest('[data-quantity-step]');
        if (!button) return;
        const input = this.querySelector('input');
        const previous = input.value;
        if (Number(button.dataset.quantityStep) > 0) input.stepUp();
        else input.stepDown();
        if (input.value !== previous) input.dispatchEvent(new Event('change', { bubbles: true }));
      });
    }
  }

  customElements.define('quantity-input', QuantityInput);

  /* Cart (drawer and page)
     ------------------------------------------------------------------------ */

  class CartItems extends HTMLElement {
    connectedCallback() {
      this.addEventListener('change', (event) => {
        const input = event.target.closest('[data-cart-qty]');
        if (input) {
          this.updateQuantity(input.dataset.key, Math.max(0, parseInt(input.value, 10) || 0), input);
          return;
        }
        const note = event.target.closest('[data-cart-note]');
        if (note) postJSON(`${routes.cart_update_url}.js`, { note: note.value });
      });

      this.addEventListener('click', (event) => {
        const remove = event.target.closest('[data-cart-remove]');
        if (!remove) return;
        event.preventDefault();
        this.updateQuantity(remove.dataset.key, 0);
      });
    }

    get count() {
      const cart = this.querySelector('[data-cart-count]');
      return cart ? Number(cart.dataset.cartCount) : 0;
    }

    render(html) {
      const fresh = parseHTML(html).querySelector('cart-items');
      if (!fresh) return;
      this.innerHTML = fresh.innerHTML;
      updateCartCount(this.count);
    }

    showError(message) {
      const error = this.querySelector('[data-cart-error]');
      if (!error) return;
      error.textContent = message;
      error.hidden = false;
    }

    async updateQuantity(key, quantity, input) {
      this.classList.add('is-loading');
      try {
        const response = await postJSON(`${routes.cart_change_url}.js`, {
          id: key,
          quantity,
          sections: [this.dataset.section],
          sections_url: window.location.pathname,
        });
        const data = await response.json();
        if (!response.ok) throw new Error(data.description || data.message || strings.cart_error);
        this.render(data.sections[this.dataset.section]);
      } catch (error) {
        if (input) input.value = input.defaultValue;
        this.showError(error.message || strings.cart_error);
      } finally {
        this.classList.remove('is-loading');
      }
    }
  }

  customElements.define('cart-items', CartItems);

  /* Product form: add to cart without leaving the page
     ------------------------------------------------------------------------ */

  class ProductForm extends HTMLElement {
    connectedCallback() {
      this.form = this.querySelector('form');
      this.button = this.querySelector('[data-add-button]');
      this.error = this.querySelector('[data-form-error]');
      if (!this.form) return;
      this.form.addEventListener('submit', this.onSubmit.bind(this));
    }

    async onSubmit(event) {
      const drawer = document.getElementById('CartDrawer');
      const cartItems = drawer && drawer.querySelector('cart-items');
      // Without the drawer the form posts normally and Shopify redirects to the cart.
      if (!cartItems) return;

      event.preventDefault();
      if (this.button.disabled || this.button.classList.contains('is-loading')) return;

      this.button.classList.add('is-loading');
      this.error.hidden = true;

      const body = new FormData(this.form);
      body.append('sections', cartItems.dataset.section);
      body.append('sections_url', window.location.pathname);

      try {
        const response = await fetch(`${routes.cart_add_url}.js`, {
          method: 'POST',
          headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
          body,
        });
        const data = await response.json();
        if (!response.ok) throw new Error(data.description || data.message || strings.cart_error);
        cartItems.render(data.sections[cartItems.dataset.section]);
        drawer.open(this.button);
      } catch (error) {
        this.error.textContent = error.message || strings.cart_error;
        this.error.hidden = false;
      } finally {
        this.button.classList.remove('is-loading');
      }
    }
  }

  customElements.define('product-form', ProductForm);

  /* Variant picker
     ------------------------------------------------------------------------ */

  class VariantPicker extends HTMLElement {
    connectedCallback() {
      this.variants = JSON.parse(this.querySelector('[data-variants]').textContent);
      this.addEventListener('change', () => {
        const selected = [...this.querySelectorAll('fieldset')].map((fieldset) => {
          const checked = fieldset.querySelector('input:checked');
          const label = fieldset.querySelector('[data-selected-value]');
          if (checked && label) label.textContent = checked.value;
          return checked ? checked.value : null;
        });
        const variant = this.variants.find((item) => item.options.every((value, index) => value === selected[index]));
        this.dispatchEvent(new CustomEvent('variant:change', { bubbles: true, detail: { variant } }));
      });
    }
  }

  customElements.define('variant-picker', VariantPicker);

  class ProductInfo extends HTMLElement {
    connectedCallback() {
      this.request = 0;
      this.addEventListener('variant:change', (event) => this.onVariantChange(event.detail.variant));
    }

    onVariantChange(variant) {
      const button = this.querySelector('[data-add-button]');
      const label = this.querySelector('[data-add-label]');
      const payment = this.querySelector('[data-payment-button]');

      if (!variant) {
        this.request += 1;
        if (button) button.disabled = true;
        if (label) label.textContent = strings.unavailable;
        if (payment) payment.hidden = true;
        return;
      }

      const idInput = this.querySelector('form input[name="id"]');
      if (idInput) idInput.value = variant.id;

      if (variant.featured_media) {
        const gallery = this.querySelector('media-gallery');
        if (gallery) gallery.show(variant.featured_media.id);
      }

      const url = new URL(window.location.href);
      url.searchParams.set('variant', variant.id);
      window.history.replaceState({}, '', url);

      // Re-render the price, availability and picker state from the server.
      const request = (this.request += 1);
      fetch(`${this.dataset.url}?variant=${variant.id}&section_id=${this.dataset.sectionId}`)
        .then((response) => response.text())
        .then((html) => {
          if (request !== this.request) return;
          const fresh = parseHTML(html);
          const focusedId = document.activeElement && document.activeElement.id;

          this.querySelectorAll('[data-swap]').forEach((region) => {
            const source = fresh.querySelector(`[data-swap="${region.dataset.swap}"]`);
            if (source) region.innerHTML = source.innerHTML;
          });

          if (focusedId && this.contains(document.getElementById(focusedId))) {
            document.getElementById(focusedId).focus({ preventScroll: true });
          }

          const freshButton = fresh.querySelector('[data-add-button]');
          const freshLabel = fresh.querySelector('[data-add-label]');
          if (button && freshButton) button.disabled = freshButton.disabled;
          if (label && freshLabel) label.textContent = freshLabel.textContent.trim();
          if (payment && freshButton) payment.hidden = freshButton.disabled;
        })
        .catch(() => {});
    }
  }

  customElements.define('product-info', ProductInfo);

  /* Product gallery
     ------------------------------------------------------------------------ */

  class MediaGallery extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-track]');
      this.slides = [...this.track.querySelectorAll('[data-media-id]')];
      this.thumbStrip = this.querySelector('[data-thumbs]');
      this.thumbs = [...this.querySelectorAll('[data-thumb]')];
      this.prev = this.querySelector('[data-gallery-prev]');
      this.next = this.querySelector('[data-gallery-next]');
      this.index = 0;

      this.thumbs.forEach((thumb) => thumb.addEventListener('click', () => this.show(thumb.dataset.thumb)));
      if (this.prev) this.prev.addEventListener('click', () => this.go(this.index - 1));
      if (this.next) this.next.addEventListener('click', () => this.go(this.index + 1));

      let frame;
      this.track.addEventListener(
        'scroll',
        () => {
          cancelAnimationFrame(frame);
          frame = requestAnimationFrame(() => this.sync());
        },
        { passive: true }
      );
      this.sync();
    }

    go(index) {
      const slide = this.slides[Math.max(0, Math.min(index, this.slides.length - 1))];
      if (slide) this.show(slide.dataset.mediaId);
    }

    show(mediaId) {
      const slide = this.slides.find((item) => item.dataset.mediaId === String(mediaId));
      if (!slide) return;
      this.track.scrollTo({ left: slide.offsetLeft, behavior: reducedMotion.matches ? 'auto' : 'smooth' });
    }

    sync() {
      const width = this.track.clientWidth || 1;
      this.index = Math.round(this.track.scrollLeft / width);
      const current = this.slides[this.index];
      if (!current) return;

      this.thumbs.forEach((thumb) => {
        const active = thumb.dataset.thumb === current.dataset.mediaId;
        if (active) thumb.setAttribute('aria-current', 'true');
        else thumb.removeAttribute('aria-current');
        if (active && this.thumbStrip) {
          this.thumbStrip.scrollTo({
            left: thumb.offsetLeft - this.thumbStrip.clientWidth / 2 + thumb.clientWidth / 2,
            behavior: reducedMotion.matches ? 'auto' : 'smooth',
          });
        }
      });

      if (this.prev) this.prev.disabled = this.index === 0;
      if (this.next) this.next.disabled = this.index >= this.slides.length - 1;
    }
  }

  customElements.define('media-gallery', MediaGallery);

  /* Related products
     ------------------------------------------------------------------------ */

  class ProductRecommendations extends HTMLElement {
    connectedCallback() {
      if (this.querySelector('.product-grid') || !this.dataset.url) return;
      const observer = new IntersectionObserver(
        (entries) => {
          if (!entries[0].isIntersecting) return;
          observer.disconnect();
          fetch(this.dataset.url)
            .then((response) => response.text())
            .then((html) => {
              const fresh = parseHTML(html).querySelector('product-recommendations');
              if (fresh && fresh.innerHTML.trim()) this.innerHTML = fresh.innerHTML;
            })
            .catch(() => {});
        },
        { rootMargin: '0px 0px 600px 0px' }
      );
      observer.observe(this);
    }
  }

  customElements.define('product-recommendations', ProductRecommendations);

  /* Small behaviours
     ------------------------------------------------------------------------ */

  // Selects that submit their form straight away (sort order, country).
  document.addEventListener('change', (event) => {
    const field = event.target.closest('[data-submit-on-change]');
    if (field && field.form) field.form.submit();
  });

  // Close the filter panel when clicking outside of it.
  document.addEventListener('click', (event) => {
    document.querySelectorAll('.facets[open]').forEach((facets) => {
      if (!facets.contains(event.target)) facets.open = false;
    });
  });

  // Reveal on scroll.
  const initReveal = (root = document) => {
    const items = [...root.querySelectorAll('[data-reveal]:not(.is-visible)')];
    if (!items.length) return;

    const designMode = window.Shopify && window.Shopify.designMode;
    if (designMode || reducedMotion.matches || !('IntersectionObserver' in window)) {
      items.forEach((item) => item.classList.add('is-visible'));
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries
          .filter((entry) => entry.isIntersecting)
          .forEach((entry, index) => {
            entry.target.style.setProperty('--reveal-index', index);
            entry.target.classList.add('is-visible');
            observer.unobserve(entry.target);
          });
      },
      { rootMargin: '0px 0px -8% 0px' }
    );
    items.forEach((item) => observer.observe(item));
  };

  document.documentElement.classList.add('reveal-ready');
  initReveal();
  document.addEventListener('shopify:section:load', (event) => initReveal(event.target));
})();
